import { useRef } from 'react';

import {
  readCoachNotificationPreferences,
  updateCoachNotificationPreferences,
} from '@/features/coach/services/notification-preferences';
import { homeKeys } from '@/features/home/queries/query-keys';
import { useSessionStore } from '@/stores';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CoachNotificationPreferencesState } from '../components/notification-preferences';
import { coachKeys } from './query-keys';

export function useCoachNotificationPreferencesQuery() {
  const coachId = useSessionStore(state => state.user?.uid);

  return useQuery({
    queryKey: coachKeys.notificationPreferences(coachId),
    queryFn: readCoachNotificationPreferences,
    enabled: Boolean(coachId),
    // The API has no GET — reading is a PATCH with an empty body (see the service). Refetching on
    // every mount/focus therefore fires a *write* that races the coach's own toggles and can land
    // pre-toggle values back in the cache (switch snapping back). Our mutations keep this cache
    // authoritative, so only fetch when the data is genuinely cold.
    staleTime: 5 * 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Saves toggles with a partial-merge PATCH (only changed keys are sent).
 *
 * No optimistic cache write lives here on purpose: the screen owns the optimistic state (see
 * `useCoachNotificationPreferences`) so the switch flips synchronously with the tap. Writing here
 * as well would mean two sources of truth fighting over the same cache entry.
 *
 * Responses are applied last-write-wins by request id: toggling quickly starts overlapping PATCHes
 * whose responses can arrive out of order, and an older full-state response must not overwrite a
 * newer one.
 */
export function useUpdateCoachNotificationPreferencesMutation() {
  const coachId = useSessionStore(state => state.user?.uid);
  const queryClient = useQueryClient();
  const queryKey = coachKeys.notificationPreferences(coachId);
  const requestSeqRef = useRef(0);
  const latestSettledSeqRef = useRef(0);

  return useMutation({
    mutationFn: updateCoachNotificationPreferences,
    onMutate: () => {
      // Stops an in-flight read from resolving *after* this write and putting pre-toggle values
      // back in the cache. Intentionally not awaited: unlike the textbook optimistic-update recipe,
      // nothing below writes to the cache, so there is nothing to sequence behind it — awaiting
      // would only stall the mutation behind an unrelated request. The returned promise never
      // rejects (react-query swallows cancellations internally), so it needs no error handling.
      queryClient.cancelQueries({ queryKey });

      requestSeqRef.current += 1;
      return { seq: requestSeqRef.current };
    },
    onSuccess: (saved, _update, context) => {
      const seq = context?.seq ?? 0;
      // Drop responses that were overtaken while in flight.
      if (seq < latestSettledSeqRef.current) return;
      latestSettledSeqRef.current = seq;

      queryClient.setQueryData<CoachNotificationPreferencesState>(queryKey, saved);

      // Only the newest response refreshes the coach home summary, so a burst of toggles triggers
      // one refetch instead of one per tap.
      if (seq === requestSeqRef.current) {
        queryClient.invalidateQueries({ queryKey: homeKeys.coach(coachId) });
      }
    },
  });
}
